import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../firebase';
import './DeliveryPersonManagement.css';

const DeliveryPersonForm = ({ person, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: person ? person.name : '',
    contactNumber: person ? person.contactNumber : '',
    email: person ? person.email : '',
    address: person ? person.address : '',
    vehicleNumber: person ? person.vehicleNumber : '',
    vehicleModel: person ? person.vehicleModel : '',
    role: person ? (person.role || person.rolafd || 'Delivery Person') : 'Delivery Person', // Default to 'Delivery Person'
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!person) {
      if (formData.password.length < 6) {
        alert("Password must be at least 6 characters long.");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        alert("Passwords do not match!");
        return;
      }
    }
    // Removed the role validation here since it's now a default/readonly field
    onSave(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{person ? 'Edit Delivery Person' : 'Add Delivery Person'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Contact Number</label>
            <input type="text" name="contactNumber" value={formData.contactNumber} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={!!person} />
          </div>
          <div className="form-group">
            <label>Role</label>
            {/* Display role as a read-only input or just text */}
            <input type="text" name="role" value={formData.role} readOnly className="read-only-input" />
          </div>
          <div className="form-group">
            <label>Address</label>
            <input type="text" name="address" value={formData.address} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Vehicle Number</label>
            <input type="text" name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Vehicle Model</label>
            <input type="text" name="vehicleModel" value={formData.vehicleModel} onChange={handleChange} required />
          </div>
          {!person && (
            <>
              <div className="form-group">
                <label>Password</label>
                <input type="password" name="password" value={formData.password} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
              </div>
            </>
          )}
          <div className="modal-actions">
            <button type="submit" className="save-btn">Save</button>
            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// The DeliveryPersonManagement component remains the same as previously provided,
// since the changes were focused on the DeliveryPersonForm.
const DeliveryPersonManagement = () => {
    const [deliveryPersons, setDeliveryPersons] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPerson, setCurrentPerson] = useState(null);
    const [loading, setLoading] = useState(true);
  
    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        try {
          const personsCollectionRef = collection(db, "delivery_persons");
          const personsSnapshot = await getDocs(personsCollectionRef);
          
          setDeliveryPersons(personsSnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              ...data,
              id: doc.id,
              role: data.role || data.rolafd || 'Delivery Person'
            };
          }));
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, []);
  
    const getDeliveryPersons = async () => {
      const personsCollectionRef = collection(db, "delivery_persons");
      const data = await getDocs(personsCollectionRef);
      setDeliveryPersons(data.docs.map((doc) => ({ 
        ...doc.data(), 
        id: doc.id,
        role: doc.data().role || doc.data().rolafd || 'Delivery Person'
      })));
    };
  
    const handleOpenModal = (person = null) => {
      setCurrentPerson(person);
      setIsModalOpen(true);
    };
  
    const handleCloseModal = () => {
      setIsModalOpen(false);
      setCurrentPerson(null);
    };
  
    const handleSave = async (personData) => {
      try {
        if (currentPerson) {
          const personDoc = doc(db, "delivery_persons", currentPerson.id);
          const { email, password, confirmPassword, ...dataToUpdate } = personData;
          await updateDoc(personDoc, {
            ...dataToUpdate,
            role: personData.role
          });
          alert("Delivery person updated successfully!");
        } else {
          const { email, password, confirmPassword, ...dataToSave } = personData;
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;
          
          await addDoc(collection(db, "delivery_persons"), {
            ...dataToSave,
            role: personData.role,
            email,
            uid: user.uid,
            createdAt: new Date()
          });
          alert("Delivery person added successfully.");
        }
        getDeliveryPersons();
        handleCloseModal();
      } catch (error) {
        console.error("Error saving person:", error);
        if (error.code === 'auth/email-already-in-use') {
          alert("This email is already registered.");
        } else if (error.code === 'auth/weak-password') {
          alert("Password must be at least 6 characters.");
        } else {
          alert("An error occurred while saving.");
        }
      }
    };
  
    const handleDelete = async (id) => {
      if (window.confirm("Are you sure you want to delete this person? This action cannot be undone.")) {
        try {
          const personDoc = doc(db, "delivery_persons", id);
          await deleteDoc(personDoc);
          alert("Delivery person deleted successfully.");
          getDeliveryPersons();
        } catch (error) {
          console.error("Error deleting person:", error);
          alert("Failed to delete delivery person.");
        }
      }
    };
  
    if (loading) return <div className="container">Loading...</div>;
  
    return (
      <div className="container">
        <h1>Delivery Persons</h1>
        <button className="add-new-btn" onClick={() => handleOpenModal()}>Add New Delivery Person</button>
        <ul className="list-view">
          {deliveryPersons.map(person => (
            <li key={person.id} className="list-item">
              <div className="person-details">
                <strong>{person.name}</strong>
                <span>Role: {person.role || 'Not Assigned'}</span>
                <span>Email: {person.email}</span>
                <span>Vehicle: {person.vehicleModel} ({person.vehicleNumber})</span>
              </div>
              <div className="actions">
                <button className="edit-btn" onClick={() => handleOpenModal(person)}>Edit</button>
                <button className="delete-btn" onClick={() => handleDelete(person.id)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
        {isModalOpen && (
          <DeliveryPersonForm 
            person={currentPerson} 
            onSave={handleSave} 
            onClose={handleCloseModal} 
          />
        )}
      </div>
    );
  };
  

export default DeliveryPersonManagement;